// Inactivity Manager for Auto-Logout Security Feature
export class InactivityManager {
  private timeoutId: NodeJS.Timeout | null = null;
  private warningTimeoutId: NodeJS.Timeout | null = null;
  private lastActivityTime: number = Date.now();
  private isWarningShown: boolean = false;
  private onWarning: () => void = () => {};
  private onLogout: () => void = () => {};
  
  // Timeout configurations
  private readonly INACTIVITY_TIMEOUT: number;
  private readonly WARNING_TIMEOUT: number;
  
  constructor() {
    // Production-ready timeouts: 60 minutes of inactivity, 5 minutes warning
    this.INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes (1 hour)
    this.WARNING_TIMEOUT = 5 * 60 * 1000;     // 5 minutes warning before logout
    console.log('🔒 Inactivity Manager: 60min inactivity timeout, 5min warning before logout');
  }
  
  // Initialize the inactivity manager
  initialize(onWarning: () => void, onLogout: () => void) {
    // Prevent re-initialization if already initialized
    if (this['_initialized']) {
      // Just update callbacks if already initialized
      this.onWarning = onWarning;
      this.onLogout = onLogout;
      return;
    }
    
    this.onWarning = onWarning;
    this.onLogout = onLogout;
    
    // Track user activity events
    this.setupActivityListeners();
    
    // Setup browser close detection
    this.setupBrowserCloseDetection();
    
    // Start the initial timeout
    this.resetTimeout();
    
    this['_initialized'] = true;
    console.log('🔒 Inactivity Manager initialized');
  }
  
  // Setup browser close detection
  private setupBrowserCloseDetection() {
    // Clear session data when browser/tab is closed
    window.addEventListener('beforeunload', () => {
      console.log('🔒 Browser closing - session will be cleared');
      // Mark that the browser is closing
      sessionStorage.setItem('browser-closing', 'true');
    });
    
    // Check if we're coming back from a closed tab
    window.addEventListener('load', () => {
      const wasClosing = sessionStorage.getItem('browser-closing');
      if (wasClosing === 'true') {
        console.log('🔒 Browser was closed - clearing session');
        sessionStorage.removeItem('browser-closing');
        // Session will be validated by the auth system
      }
    });
  }
  
  // Setup event listeners for user activity
  private setupActivityListeners() {
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'submit' // Track form submissions as activity
    ];
    
    events.forEach(event => {
      document.addEventListener(event, () => {
        this.resetActivity();
      }, true);
    });
  }
  
  // Public method to reset activity (can be called from API requests, mutations, etc.)
  public recordActivity() {
    this.resetActivity();
  }
  
  // Reset activity and restart timeout
  private resetActivity() {
    this.lastActivityTime = Date.now();
    this.isWarningShown = false;
    
    // Clear existing timeouts
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
    }
    
    // Restart timeout
    this.resetTimeout();
  }
  
  // Reset the inactivity timeout
  private resetTimeout() {
    this.timeoutId = setTimeout(() => {
      this.handleInactivity();
    }, this.INACTIVITY_TIMEOUT - this.WARNING_TIMEOUT);
  }
  
  // Handle inactivity - show warning
  private handleInactivity() {
    if (this.isWarningShown) {
      return; // Warning already shown
    }
    
    console.log('⚠️ User inactive - showing warning dialog');
    this.isWarningShown = true;
    this.onWarning();
    
    // Set final logout timeout
    this.warningTimeoutId = setTimeout(() => {
      this.handleLogout();
    }, this.WARNING_TIMEOUT);
  }
  
  // Handle final logout
  private handleLogout() {
    console.log('🔒 Auto-logout triggered due to inactivity');
    this.onLogout();
  }
  
  // Extend session (called when user interacts with warning dialog)
  extendSession() {
    console.log('✅ Session extended by user');
    this.resetActivity();
  }
  
  // Force logout (called when user confirms logout)
  forceLogout() {
    console.log('🔒 Force logout confirmed by user');
    this.onLogout();
  }
  
  // Get remaining time until warning (for debugging)
  getRemainingTime(): number {
    const timeSinceActivity = Date.now() - this.lastActivityTime;
    const timeUntilWarning = this.INACTIVITY_TIMEOUT - this.WARNING_TIMEOUT - timeSinceActivity;
    return Math.max(0, timeUntilWarning);
  }
  
  // Get remaining time until logout (for debugging)
  getWarningTime(): number {
    if (!this.isWarningShown) {
      return 0;
    }
    const timeSinceWarning = Date.now() - (this.lastActivityTime + (this.INACTIVITY_TIMEOUT - this.WARNING_TIMEOUT));
    return Math.max(0, this.WARNING_TIMEOUT - timeSinceWarning);
  }
  
  // Cleanup
  destroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
    this['_initialized'] = false;
    console.log('🔒 Inactivity Manager destroyed');
  }
}

// Export singleton instance
export const inactivityManager = new InactivityManager();
